<script setup lang="ts">
//
// Imports
//

import { useDisplay } from "vuetify";
import { useAuthClient } from "~/lib/auth-client";

//
// Initial Setup
//

// Router & route
const route = useRoute();
const router = useRouter();

// Localization
const { locale, t } = useI18n();
const localePath = useLocalePath();
const switchLocalePath = useSwitchLocalePath();
const selectedLanguage = ref<string>(locale.value);

// API Client
let cl = await useProdClient();
const ac = new AbortController();

// Screen size
const { smAndDown } = useDisplay();

// Props
const props = defineProps({
  /**
   * The layout type to determine the visual arrangement of the component
   * @type {string} - The layout style of the navbar.
   * @required false
   */
  layout: {
    type: String,
    required: false,
  },
});

//
// Composables
//

// Auth
const { logOut, accessTokenTimeLeft, refreshTokenTimeLeft } = useAuthClient();

//
// Reactive Data
//

// Get the user info
const userInfo = inject("userInfo");

// Check if the layout is 'corporate'
const isDoctorClinicLayout = computed(() => props.layout === "corporate");

// Drawer
const drawerOpen = ref(false);
const menuItems = computed(() => [
  {
    title: t("navigation_drawer.all_patients"),
    path: localePath("/home"),
    icon: "mdi-account-outline",
  },
  {
    title: t("navigation_drawer.new_results"),
    path: localePath("/new-results"),
    icon: "mdi-folder-account-outline",
  },
  {
    title: t("navigation_drawer.search"),
    path: localePath("/search"),
    icon: "mdi-magnify",
  },
  {
    title: t("navigation_drawer.starred"),
    path: localePath("/starred"),
    icon: "mdi-star-outline",
  },
]);

// Laboratory Logo
const loadingLabLogo = ref<boolean>(true);
const format = ref<"svg" | "png">("svg");
const logoUrl = ref<string>("");

// Laboratory Name
const loadingLabName = ref<boolean>(true);
const laboratoryName = ref<string>("");

//
// Methods
//

/**
 * Switches the app locale and updates the path to the corresponding language version.
 * @param lang - The language code to switch to ("en" or "bg").
 */
const changeLanguage = (lang: "en" | "bg"): void => {
  const newPath = switchLocalePath(lang);
  if (newPath) {
    selectedLanguage.value = lang;
    router.push(newPath);
  }
};

/**
 * Determines if the provided path is currently active
 */
const isActive = (path: string) => {
  const localizedPath = localePath(path);
  return route.path === path || route.path === localizedPath;
};

/**
 * Navigates to the specified path and closes the drawer
 * @param {string} path - The path to navigate to.
 */
const navigateAndClose = (path: string) => {
  const localizedPath = localePath(path);
  router.push(localizedPath);
};

/**
 * Fetches the laboratory logo for the given format and updates the state.
 * @param format - The format of the logo to fetch (e.g., "svg", "png").
 */
const fetchLabLogo = async (format: "svg" | "png"): Promise<void> => {
  try {
    loadingLabLogo.value = true;

    // Fetch and update the laboratory logo
    logoUrl.value = await cl.fetchLaboratoryLogo(format, ac.signal);
  } catch (err) {
    console.error("Error fetching laboratory logo:", err);
  } finally {
    loadingLabLogo.value = false;
  }
};

/**
 * Fetches the laboratory name for the given locale and updates the state.
 * @param locale - The locale code to fetch the laboratory name for (e.g., "en", "bg").
 */
const fetchLabName = async (locale: string) => {
  try {
    loadingLabName.value = true;

    // Fetch and update the laboratory name
    laboratoryName.value = await cl.fetchLaboratoryName(locale, ac.signal);
  } catch (err) {
    console.error("Error fetching laboratory name:", err);
  } finally {
    loadingLabName.value = false;
  }
};

//
// On Component Mount
//

// Laboratory logo
onMounted(() => fetchLabLogo(format.value));

// Laboratory name
onMounted(() => fetchLabName(locale.value));

// Drawer state
onMounted(() => {
  if (typeof window !== "undefined") {
    const savedState = localStorage.getItem("drawerOpen");
    if (savedState !== null) {
      drawerOpen.value = JSON.parse(savedState);
    } else {
      // Default to true if not smAndDown
      drawerOpen.value = !smAndDown.value;
    }
  }
});

//
// Watches
//

// Watch for locale changes and fetch the laboratory name again
watch(
  () => locale.value,
  (newLocale) => fetchLabName(newLocale)
);

// Watch for changes to the drawer state
watch(drawerOpen, (newValue) => {
  if (typeof window !== "undefined") {
    localStorage.setItem("drawerOpen", JSON.stringify(newValue));
  }
});

//
// Metadata
//

// Set the page's head metadata
useHead({
  link: [{ rel: "icon", type: "image/svg+xml", href: logoUrl }],
  title: laboratoryName,
  htmlAttrs: {
    lang: locale,
  },
  meta: [
    {
      name: "description",
      content:
        "A comprehensive platform for managing medical laboratory operations, patient data, and test results.",
    },
    {
      name: "keywords",
      content: "medical laboratory, patient data, test results, healthcare",
    },
    { name: "author", content: "SKYWARE Group | LIMS iLab" },
    { name: "viewport", content: "width=device-width, initial-scale=1" },
  ],
});
</script>

<template>
  <client-only>
    <!-- App Bar -->
    <v-app-bar
      app
      class="bg-white w-100"
      style="border-bottom: 1px solid #e0e0e0"
    >
      <div class="d-flex align-center justify-space-between w-100">
        <div class="d-flex align-center ga-2 w-100">
          <!-- Drawer Toggle -->
          <v-tooltip
            v-if="isDoctorClinicLayout"
            location="bottom"
            :open-delay="700"
            :close-delay="300"
          >
            <template v-slot:activator="{ props: attrs }">
              <v-app-bar-nav-icon
                v-bind="attrs"
                color="primary"
                v-on:click="drawerOpen = !drawerOpen"
              />
            </template>
            <span>{{
              drawerOpen
                ? t("tooltips.navbar.close_menu")
                : t("tooltips.navbar.open_menu")
            }}</span>
          </v-tooltip>

          <!-- Logo and Lab Name -->
          <div class="d-flex ga-2 pl-4 w-100">
            <v-img
              :src="logoUrl"
              alt="Laboratory Logo"
              style="max-width: 30px; height: auto"
              loading="lazy"
              class="cursor-pointer"
              aria-label="Go to home page"
              v-tooltip:bottom="{
                text: t('tooltips.navbar.logo'),
                openDelay: 700,
              }"
              @click="
                isDoctorClinicLayout
                  ? $router.push(localePath('/home'))
                  : $router.push(localePath('/otp-home'))
              "
            ></v-img>

            <NuxtLink
              :to="
                isDoctorClinicLayout
                  ? localePath('/home')
                  : localePath('/otp-home')
              "
              tabindex="0"
              class="text-black text-decoration-none"
              aria-label="Go to home page"
              v-tooltip:bottom="{
                text: t('tooltips.navbar.logo'),
                openDelay: 700,
              }"
            >
              <span class="text-h6 font-weight-bold">{{
                loadingLabName ? "Loading..." : laboratoryName
              }}</span>
            </NuxtLink>
          </div>
        </div>

        <!-- Controls -->
        <div class="d-flex align-center">
          <!-- User Dropdown -->
          <v-menu open-on-click location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                icon
                v-bind="props"
                color="primary"
                tabindex="0"
                aria-label="Account Info"
                v-tooltip:bottom="{
                  text:
                    locale === 'bg' ? 'Детайли за акаунта' : 'Account Details',
                  openDelay: 700,
                }"
              >
                <v-icon
                  :icon="
                    isDoctorClinicLayout
                      ? 'mdi-account-details-outline'
                      : 'mdi-account-cog-outline'
                  "
                  size="24"
                ></v-icon>
              </v-btn>
            </template>

            <v-list
              nav
              density="comfortable"
              class="d-flex flex-column ga-2 px-0"
            >
              <!-- Avatar -->
              <NuxtLink
                v-if="isDoctorClinicLayout"
                :to="
                  isDoctorClinicLayout
                    ? localePath('/account')
                    : localePath('/portal/otp-patient/account')
                "
                class="text-decoration-none text-black"
              >
                <v-list-item class="px-4">
                  <!-- Initials -->
                  <template #prepend>
                    <v-avatar
                      :color="
                        getColorForInitials(
                          (userInfo?.given_name?.[0] || 'U') +
                            (userInfo?.family_name?.[0] || 'N')
                        )
                      "
                      size="small"
                      style="font-size: 0.7rem; color: white"
                    >
                      <strong>
                        {{ userInfo?.given_name[0] ?? "U" }}
                        {{ userInfo?.family_name[0] ?? "N" }}
                      </strong>
                    </v-avatar>
                  </template>

                  <!-- Full name -->
                  <v-list-item-title>
                    <span>
                      {{ userInfo?.given_name ?? "Unknown" }}
                      {{ userInfo?.family_name ?? "User" }}
                    </span>
                  </v-list-item-title>

                  <!-- Email -->
                  <v-list-item-subtitle>
                    <span v-if="isDoctorClinicLayout">
                      {{ userInfo?.email ?? "Email not available" }}
                    </span>
                  </v-list-item-subtitle>
                </v-list-item>
              </NuxtLink>

              <!-- Divider -->
              <v-divider v-if="isDoctorClinicLayout"></v-divider>

              <!-- Dropdown items -->
              <div class="d-flex flex-column w-100 justify-start ga-0 px-4">
                <NuxtLink
                  v-if="isDoctorClinicLayout"
                  :to="localePath('/account')"
                  class="text-decoration-none text-black opacity-80"
                >
                  <v-list-item
                    class="d-flex align-center"
                    rounded="lg"
                    value="Account"
                    color="primary"
                    :title="t('tooltips.navbar.account')"
                    prepend-icon="mdi-account-outline"
                  >
                  </v-list-item>
                </NuxtLink>

                <v-list-group value="Languages">
                  <template v-slot:activator="{ props }">
                    <v-list-item
                      v-bind="props"
                      :title="t('tooltips.navbar.language')"
                      prepend-icon="mdi-web"
                      aria-label="Select language"
                      rounded="lg"
                    >
                    </v-list-item>
                  </template>

                  <v-list-item
                    value="en"
                    rounded="lg"
                    @click="changeLanguage('en')"
                    aria-label="Select English"
                  >
                    <v-list-item-title class="d-flex align-center ga-2">
                      <CircleFlags
                        country="us"
                        class="mr-2"
                        style="width: 20px; height: 20px"
                      />
                      <span
                        :class="{
                          'font-weight-bold': selectedLanguage === 'en',
                        }"
                      >
                        English
                      </span>
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    value="bg"
                    rounded="lg"
                    @click="changeLanguage('bg')"
                    aria-label="Select Bulgarian"
                  >
                    <v-list-item-title class="d-flex align-center ga-2">
                      <CircleFlags
                        country="bg"
                        class="mr-2"
                        style="width: 20px; height: 20px"
                      />
                      <span
                        :class="{
                          'font-weight-bold': selectedLanguage === 'bg',
                        }"
                      >
                        Bulgarian
                      </span>
                    </v-list-item-title>
                  </v-list-item>
                </v-list-group>

                <NuxtLink
                  @click="logOut()"
                  class="text-decoration-none text-black opacity-80"
                >
                  <v-list-item
                    class="d-flex align-center"
                    rounded="lg"
                    value="Logout"
                    color="primary"
                    :title="t('tooltips.navbar.logout')"
                    prepend-icon="mdi-logout"
                  >
                  </v-list-item>
                </NuxtLink>
              </div>
            </v-list>
          </v-menu>
        </div>
      </div>
    </v-app-bar>

    <v-navigation-drawer
      v-if="isDoctorClinicLayout"
      app
      v-model="drawerOpen"
      :mobile="smAndDown"
      class="bg-white"
    >
      <!-- Avatar -->
      <v-list>
        <NuxtLink
          :to="localePath('/account')"
          class="text-decoration-none text-black"
        >
          <v-list-item class="d-flex align-center">
            <!-- Initials -->
            <template #prepend>
              <v-avatar
                :color="
                  getColorForInitials(
                    (userInfo?.given_name?.[0] || 'U') +
                      (userInfo?.family_name?.[0] || 'N')
                  )
                "
                style="font-size: 0.8rem; color: white"
              >
                <strong>
                  {{ userInfo?.given_name[0] ?? "U" }}
                  {{ userInfo?.family_name[0] ?? "N" }}
                </strong>
              </v-avatar>
            </template>

            <!-- Full Name -->
            <v-list-item-title>
              {{ userInfo?.given_name ?? "Unknown" }}
              {{ userInfo?.family_name ?? "User" }}
            </v-list-item-title>

            <!-- Email -->
            <v-list-item-subtitle>
              {{ userInfo?.email ?? "Email not available" }}
            </v-list-item-subtitle>
          </v-list-item>
        </NuxtLink>
      </v-list>

      <!-- Divider -->
      <v-divider />

      <!-- Menu items -->
      <v-list density="compact" nav>
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          @click="navigateAndClose?.(item.path)"
          rounded="lg"
          :class="[isActive?.(item.path) && 'bg-grey-lighten-2']"
        >
          <!-- Item icon -->
          <template #prepend>
            <v-icon
              :icon="item.icon"
              :class="
                isActive?.(item.path)
                  ? 'text-black opacity-100'
                  : 'text-grey-darken-4'
              "
            >
            </v-icon>
          </template>

          <!-- Item text -->
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item>
      </v-list>

      <v-divider></v-divider>

      <!-- Access/Refresh tokens time -->
      <v-list density="compact" nav>
        <!-- Access token -->
        <v-list-item rounded="lg">
          <!-- Item icon -->
          <template #prepend>
            <v-icon icon="mdi-console" class="text-grey-darken-4"></v-icon>
          </template>

          <v-list-item-title>
            Access token: {{ accessTokenTimeLeft ?? "N/A" }}
          </v-list-item-title>
        </v-list-item>

        <!-- Refresh token -->
        <v-list-item rounded="lg">
          <!-- Item icon -->
          <template #prepend>
            <v-icon icon="mdi-refresh" class="text-grey-darken-4"></v-icon>
          </template>

          <v-list-item-title>
            Refresh token: {{ refreshTokenTimeLeft ?? "N/A" }}
          </v-list-item-title>
        </v-list-item>
      </v-list>

      <!-- About page -->
      <template v-slot:append>
        <v-divider></v-divider>
        <div class="px-2">
          <v-list density="compact" nav>
            <v-list-item
              rounded="lg"
              :class="[isActive?.(localePath('/about')) && 'bg-grey-lighten-2']"
              @click="navigateAndClose?.(localePath('/about'))"
            >
              <!-- Item icon -->
              <template #prepend>
                <v-icon
                  icon="mdi-information-outline"
                  :class="
                    isActive?.(localePath('/about'))
                      ? 'text-black opacity-100'
                      : 'text-grey-darken-4'
                  "
                ></v-icon>
              </template>

              <v-list-item-title>
                {{ t("page_titles.about_page") }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </div>
      </template>
    </v-navigation-drawer>
  </client-only>
</template>
